import { useState, useCallback, useRef, useEffect } from 'react';
import { useRequest } from 'apis';
import { messageService } from 'services';
import useCallbackDebounce from 'components/hooks/useCallbackDebounce';
import { useTranslationX } from 'i18n';

export const useControleAporte = (onDataRefresh?: () => void) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aportesSelecionados, setAportesSelecionados] = useState<Set<string>>(new Set());

  const aportesSelecionadosRef = useRef(aportesSelecionados);
  const { tx } = useTranslationX('dashboard', 'finance');

  const request = useRequest();

  useEffect(() => {
    aportesSelecionadosRef.current = aportesSelecionados;
  }, [aportesSelecionados]);

  const processarAportesAcumulados = useCallback(async () => {
    const aportesParaProcessar = Array.from(aportesSelecionadosRef.current);

    if (aportesParaProcessar.length === 0) {
      return;
    }

    setLoading(true);

    try {
      const aporteHashes = aportesParaProcessar;

      const resultado = await new Promise<{ success: boolean; data?: any; error?: any }>(
        resolve => {
          request({
            url: '/controle-alertas/ocultar',
            method: 'POST',
            data: {
              alertaHashes: aporteHashes,
              tipo: 'APORTE',
            },
            onSuccess: data => {
              resolve({ success: true, data });
            },
            onError: error => {
              resolve({ success: false, error });
            },
          });
        }
      );

      if (resultado.success) {
        const { sucessos = aporteHashes.length, falhas = 0 } = resultado.data || {};

        setAportesSelecionados(new Set());

        if (sucessos > 0) {
          const mensagem =
            sucessos === 1
              ? tx('nonCovered.successSingle')
              : tx('nonCovered.successMultiple', { count: sucessos });
          messageService.success(mensagem, { duration: 3000 });
        }

        if (falhas > 0) {
          const mensagem =
            falhas === 1
              ? tx('nonCovered.errorSingle')
              : tx('nonCovered.errorMultiple', { count: falhas });
          messageService.error(mensagem, { duration: 3000 });
        }

        if (sucessos > 0 && onDataRefresh) {
          onDataRefresh();
        }
      } else {
        messageService.error(tx('nonCovered.unexpectedError'), { duration: 3000 });
        setAportesSelecionados(new Set());
      }
    } catch (error) {
      messageService.error(tx('nonCovered.unexpectedError'), { duration: 3000 });
      setAportesSelecionados(new Set());
    }

    setLoading(false);
  }, [request, onDataRefresh, tx]);

  const processarAportesDebounced = useCallbackDebounce(processarAportesAcumulados, 2000);

  const toggleAporteSelecionado = useCallback(
    (aporteId: string): boolean => {
      setError(null);

      setAportesSelecionados(prev => {
        const newSet = new Set(prev);

        if (newSet.has(aporteId)) {
          newSet.delete(aporteId);
        } else {
          newSet.add(aporteId);
        }

        return newSet;
      });

      processarAportesDebounced();

      return true;
    },
    [processarAportesDebounced]
  );

  const isAporteSelecionado = useCallback(
    (aporteId: string) => {
      return aportesSelecionados.has(aporteId);
    },
    [aportesSelecionados]
  );

  return {
    toggleAporteSelecionado,
    loading,
    error,
    isAporteSelecionado,
    aportesSelecionados,
  };
};
